/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';

// Components
import ResponseForm from 'components/Comments/CommentForm/ResponseForm';
import CommentEditActionsMenu from 'components/Comments/CommentEditActionsMenu';

// Constants
import { COMMENT_RULES } from 'constants/index';

// Hooks
import useHandleImageUpload from 'hooks/useHandleImageUpload';

// Queries
import { GET_COMMENT_FOR_EDIT, SAVE_COMMENT_UPDATE } from '../CommentQueries';

function InlineCommentEditor(props) {
  const { commentId, onClose, isModerator } = props;

  const [draft, setDraft] = useState(null);
  const [focused, setFocused] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const {
    setReviewImage,
    callDeleteImage,
    callUploadImage,
    handleRemoveExistingImage,
    setUploadingError,
    isHandlingImageUpload,
    removedExistingImage,
    uploadingError,
    reviewImage,
  } = useHandleImageUpload();

  const loggedIn = useSelector(state => state.user.logged_in);

  const { data: commentData, loading } = useQuery(GET_COMMENT_FOR_EDIT, {
    variables: {
      comment_id: commentId,
      _authenticated: true,
    },
    ssr: false,
    skip: !loggedIn,
    fetchPolicy: 'cache-first', // This result should be cached from the top level Comments component
    onError: err => {
      console.error('Failed to load comment for edit', err);
    },
  });

  const rawComment = get(commentData, 'RecipeComment.raw_text') || '';
  const type = get(commentData, 'RecipeComment.type');
  const contentId = get(commentData, 'RecipeComment.content_id');
  const userId = get(commentData, 'RecipeComment.user.id');
  const imageUrls = useMemo(() => get(commentData, 'RecipeComment.image_urls'), [commentData]);

  const [saveCommentUpdate, { loading: saveInProgress }] = useMutation(SAVE_COMMENT_UPDATE, {
    onCompleted() {
      // eslint-disable-next-line no-use-before-define
      handleFormClose();
    },
    onError: err => {
      console.error('Failed to save comment', err);
      const errorCode = get(err, 'graphQLErrors[0].extensions.code');
      const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
      const niceSaveError =
        get(err, 'graphQLErrors[0].message') ||
        `We can't save this right now. Please try again later${niceErrorCode}`;
      setSaveError(niceSaveError);
    },
    refetchQueries: ['GetAllRecipeComments'],
  });

  const handleFormClose = () => {
    setDraft(null);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleFormChange = values => {
    setDraft(values.response);
  };

  const handleFormSubmit = async values => {
    if (type === 'review') {
      if (removedExistingImage && !uploadingError) {
        await callDeleteImage(contentId, userId);
      }

      if (reviewImage) {
        await callUploadImage(contentId, userId);
      }
    }

    await saveCommentUpdate({
      variables: {
        comment_id: commentId,
        raw_text: values.response,
        moderation_status: 'approved',
      },
    });
  };

  const editOptionsMenu = commentData ? (
    <CommentEditActionsMenu
      contentId={contentId}
      userId={userId}
      commentId={commentId}
      type={type}
      onDeleteError={setSaveError}
      onDeleteCompleted={handleFormClose}
    />
  ) : null;

  return (
    <ResponseForm
      focused={focused}
      id={`response-text-${commentId}`}
      isOpen
      loading={loading}
      isModerator={isModerator}
      isEditing
      type={type}
      imageUrls={imageUrls}
      maxLength={type ? COMMENT_RULES[type].maxLength : 500}
      onBlur={() => setFocused(false)}
      onChange={handleFormChange}
      onClose={handleFormClose}
      onFocus={() => setFocused(true)}
      onSubmit={handleFormSubmit}
      optionsMenu={editOptionsMenu}
      response={typeof draft === 'string' ? draft : rawComment || ''}
      saveButtonText="Save changes"
      saveError={saveError}
      saveInProgress={saveInProgress}
      showUserInfo={false}
      textFieldLabel="Edit response"
      isUploadingImage={isHandlingImageUpload}
      setReviewImage={setReviewImage}
      setUploadingError={setUploadingError}
      onRemoveExistingImage={handleRemoveExistingImage}
      uploadingError={uploadingError}
    />
  );
}

InlineCommentEditor.propTypes = {
  commentId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  isModerator: PropTypes.bool,
};

InlineCommentEditor.defaultProps = {
  onClose: () => {},
  isModerator: false,
};
// InlineCommentEditor.whyDidYouRender = true;

export default React.memo(InlineCommentEditor);
